import { DateTime } from 'luxon';
import { MouseEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Alert from '@/components/Alert';
import { Controls, ControlsButton } from '@/components/Controls';
import ExportButton from '@/components/ExportButton';
import FiltersDropdown from '@/components/FilterDropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import FilterRadioButton from '@/components/FilterDropdown/FilterRadioButton';
import {
  FilterGroupBody,
  FilterGroupHeader,
  FilterGroupTitle,
  FilterWrap,
} from '@/components/FilterDropdown/Parts';
import Hidden from '@/components/Hidden';
import HideElements from '@/components/HideElements';
import {
  PageHeading,
  SectionHeading,
  Sections,
  TeamHeading,
} from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';
import Channels from '@/pages/Settings/components/Channels';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

import Preferences from './components/Preferences';
import Profile from './components/Profile';

type LocationState =
  | {
      activeTab?: Tabs;
    }
  | undefined;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 12px;
  ${SectionHeading} {
    margin-right: auto;
    margin-bottom: 0;
  }
`;

const StyledExportButton = styled(ExportButton)`
  margin-left: auto;
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FiltersHolder = styled.div`
  display: flex;
  margin-left: auto;
`;

const months = (() => {
  const months = [];

  let month = DateTime.now();

  while (month.diffNow('months').months > -12) {
    months.push(month);
    month = month.minus({ months: 1 });
  }

  return months;
})();

export enum Tabs {
  CompanyChannels,
  CompetitorsChannels,
  Profile,
  Preferences,
}

const SettingsPage = () => {
  const location = useLocation();

  const locationState = location.state as LocationState;

  const { teamId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const alerts = useAlerts();

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.CompanyChannels);

  const [activeMonth, setActiveMonth] = useState(months[0]);
  const [showFilters, setShowFilters] = useState(false);

  const organisationName = useSelector(
    (rootState: RootState) => rootState.auth.user!.organisation!.name,
  );

  const headings = {
    [Tabs.CompanyChannels]: `${organisationName} channels`,
    [Tabs.CompetitorsChannels]: 'Competitor channels',
    [Tabs.Profile]: 'Profile',
    [Tabs.Preferences]: 'Preferences',
  };

  const fetchCsv = async () =>
    api.get<never, string>(`/v1/teams/${teamId}/channels/export`, {
      params: {
        ...(activeTab === Tabs.CompetitorsChannels && { competitors: 1 }),
        month: activeMonth.toFormat('yyyy-MM'),
      },
    });

  const renderActiveTab = () => {
    switch (activeTab) {
      case Tabs.CompanyChannels:
        return <Channels teamId={teamId!} activeMonth={activeMonth} />;
      case Tabs.CompetitorsChannels:
        return (
          <Channels teamId={teamId!} activeMonth={activeMonth} competitors />
        );
      case Tabs.Profile:
        return <Profile />;
      case Tabs.Preferences:
        return <Preferences />;
      default:
        return null;
    }
  };

  const toggleMonthFilter = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    setShowFilters((prev) => !prev);
  };

  useEffect(() => {
    if (searchParams.get('tab') === 'bp_channels') {
      setActiveTab(Tabs.CompanyChannels);
    }

    if (searchParams.get('error') === 'invalid_channel') {
      alerts.error('Failed to authorize', 'Invalid channel selected!');
    }

    searchParams.delete('tab');
    searchParams.delete('error');

    setSearchParams(searchParams);
  }, [searchParams, alerts, setSearchParams]);

  useEffect(() => {
    locationState?.activeTab && setActiveTab(locationState?.activeTab);
  }, [locationState]);

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>Settings</PageHeading>
      </HeadingRow>
      <Sections>
        {teamId !== 'all' && (
          <Hidden>
            <Alert type="warning" headline="New Team Kickstart Checklist">
              <ul>
                <li>
                  Add team members for the whole production funnel (Research,
                  Production, Post-Production, Distribution, Overview)
                </li>
                <li>
                  Add all channels that are going to be used and their tokens
                  for data management
                </li>
                <li>
                  Add competitor channels for Content Research data management
                </li>
              </ul>
            </Alert>
          </Hidden>
        )}
        <div>
          {teamId !== 'all' && (
            <Header>
              <Controls>
                <>
                  <ControlsButton
                    active={activeTab === Tabs.CompanyChannels}
                    onClick={() => setActiveTab(Tabs.CompanyChannels)}
                  >
                    {headings[Tabs.CompanyChannels]}
                  </ControlsButton>
                  <ControlsButton
                    active={activeTab === Tabs.CompetitorsChannels}
                    onClick={() => setActiveTab(Tabs.CompetitorsChannels)}
                  >
                    {headings[Tabs.CompetitorsChannels]}
                  </ControlsButton>
                </>
                <>
                  <ControlsButton
                    active={activeTab === Tabs.Profile}
                    onClick={() => setActiveTab(Tabs.Profile)}
                  >
                    {headings[Tabs.Profile]}
                  </ControlsButton>

                  <ControlsButton
                    active={activeTab === Tabs.Preferences}
                    onClick={() => setActiveTab(Tabs.Preferences)}
                  >
                    {headings[Tabs.Preferences]}
                  </ControlsButton>
                </>
              </Controls>
              {[Tabs.CompetitorsChannels, Tabs.CompanyChannels].includes(
                activeTab,
              ) && (
                <FiltersHolder>
                  <HideElements>
                    <StyledExportButton
                      fileName="channels.csv"
                      fetchCsv={fetchCsv}
                    />
                  </HideElements>
                  <FilterWrap>
                    <FilterButton
                      isActive={showFilters}
                      onClick={toggleMonthFilter}
                      filterApplied={!!activeMonth}
                    />
                    {showFilters && (
                      <FiltersDropdown onClose={() => setShowFilters(false)}>
                        <FilterGroupHeader>
                          <FilterGroupTitle>Month</FilterGroupTitle>
                        </FilterGroupHeader>
                        <FilterGroupBody>
                          {months.map((dt, index) => (
                            <FilterRadioButton
                              key={index}
                              id={`monthRadio${index}`}
                              value={dt.toFormat('yyyy-LL')}
                              checked={
                                activeMonth.toFormat('yyyy-LL') ===
                                dt.toFormat('yyyy-LL')
                              }
                              setValue={(dateString) =>
                                setActiveMonth(
                                  DateTime.fromFormat(dateString, 'yyyy-MM'),
                                )
                              }
                            >
                              {dt.toFormat('yyyy LLLL')}
                            </FilterRadioButton>
                          ))}
                        </FilterGroupBody>
                      </FiltersDropdown>
                    )}
                  </FilterWrap>
                </FiltersHolder>
              )}
            </Header>
          )}
          {renderActiveTab()}
        </div>
      </Sections>
    </>
  );
};

export default SettingsPage;
